import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import { getCurrentTimezone } from './timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Takvim günü YYYY-MM-DD; UTC kayması yok (API ve Redux ile tutarlı). */
export const formatBirthDateOnly = (input) => {
    if (input == null || input === '') return null;
    if (typeof input === 'string') {
        const t = input.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    }
    const d = dayjs(input);
    if (!d.isValid()) return null;
    return d.format('YYYY-MM-DD');
};

/** DatePicker value (dayjs, yerel takvim). */
export const parseBirthDateForPicker = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const t = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return dayjs(t, 'YYYY-MM-DD');
    }
    const d = dayjs(value);
    return d.isValid() ? d : null;
};

/** Görüntüleme DD/MM/YYYY */
export const formatBirthDateDisplay = (value) => {
    const s = formatBirthDateOnly(value);
    if (!s) return '';
    return dayjs(s, 'YYYY-MM-DD').format('DD/MM/YYYY');
};

/**
 * Backend'den gelen tarihi kullanıcının timezone'ına çevirir
 * @param {string} backendDate - Backend'den gelen tarih (UTC)
 * @param {string} userTimezone - Kullanıcının timezone'ı (örn: "Europe/Istanbul")
 * @returns {string} Kullanıcının timezone'ında formatlanmış tarih
 */
const convertBackendDateToUserTimezone = (backendDate, userTimezone = 'UTC') => {
    if (!backendDate) return '';

    try {
        // Backend'den gelen tarihi Date objesine çevir
        const date = new Date(backendDate);

        // Kullanıcının timezone'ında formatla
        return date.toLocaleString('tr-TR', {
            timeZone: userTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch (error) {
        console.warn('Tarih dönüştürme hatası:', error);
        return backendDate;
    }
};

/**
 * Backend'den gelen tarihi kullanıcının timezone'ında sadece tarih olarak gösterir
 * @param {string} backendDate - Backend'den gelen tarih (UTC)
 * @param {string} userTimezone - Kullanıcının timezone'ı
 * @returns {string} Sadece tarih kısmı
 */
const convertBackendDateToUserDate = (backendDate, userTimezone = 'UTC') => {
    if (!backendDate) return '';

    try {
        const date = new Date(backendDate);

        return date.toLocaleDateString('tr-TR', {
            timeZone: userTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch (error) {
        console.warn('Tarih dönüştürme hatası:', error);
        return backendDate;
    }
};

/**
 * Backend'den gelen tarihi kullanıcının timezone'ında sadece saat olarak gösterir
 * @param {string} backendDate - Backend'den gelen tarih (UTC)
 * @param {string} userTimezone - Kullanıcının timezone'ı
 * @returns {string} Sadece saat kısmı
 */
export const convertBackendDateToUserTime = (backendDate, userTimezone = 'UTC') => {
    if (!backendDate) return '';

    try {
        const date = new Date(backendDate);

        return date.toLocaleTimeString('tr-TR', {
            timeZone: userTimezone,
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.warn('Saat dönüştürme hatası:', error);
        return backendDate;
    }
};

/**
 * Redux'tan doktor/kullanıcı timezone'ını alır.
 * Profil güncellemesi sonrası doctor.profile önceliklidir (JWT/auth stale kalabilir).
 * @returns {string} IANA timezone
 */
export const useUserTimezone = () => {
    const authTimezone = useSelector((state) => state.auth.user?.timeZone);
    const profileTimezone = useSelector((state) => state.doctor?.profile?.timeZone);
    return profileTimezone || authTimezone || getCurrentTimezone() || 'UTC';
};

/**
 * Hook: Backend tarihini kullanıcı timezone'ında sadece tarih olarak gösterir
 * @param {string} backendDate - Backend'den gelen tarih
 * @returns {string} Sadece tarih kısmı
 */
export const useConvertBackendDateOnly = (backendDate) => {
    const userTimezone = useUserTimezone();
    return convertBackendDateToUserDate(backendDate, userTimezone);
};

/**
 * Hook: Backend tarihini kullanıcı timezone'ında sadece saat olarak gösterir
 * @param {string} backendDate - Backend'den gelen tarih
 * @returns {string} Sadece saat kısmı
 */
export const useConvertBackendTimeOnly = (backendDate) => {
    const userTimezone = useUserTimezone();
    return convertBackendDateToUserTime(backendDate, userTimezone);
};

/** UTC ISO → kullanıcı timezone'unda dayjs (görüntüleme / gruplama). */
export const toUserTimezoneDayjs = (backendDate, userTimezone = 'UTC') => {
    if (!backendDate) return null;
    try {
        const d = dayjs.utc(backendDate).tz(userTimezone);
        return d.isValid() ? d : null;
    } catch (error) {
        console.warn('Timezone dönüştürme hatası:', error);
        return null;
    }
};

/**
 * Backend bookedDate'i kullanıcı timezone'unda dayjs formatıyla gösterir.
 * @param {string} backendDate - UTC ISO
 * @param {string} format - dayjs format string
 * @param {string} userTimezone - IANA timezone
 * @param {string} [locale] - dayjs locale (örn. "tr")
 */
export const formatBookedDateInTimezone = (
    backendDate,
    format,
    userTimezone = 'UTC',
    locale
) => {
    const d = toUserTimezoneDayjs(backendDate, userTimezone);
    if (!d) return '';
    return locale ? d.locale(locale).format(format) : d.format(format);
};

/** bookedDate + dakika, kullanıcı timezone'unda formatlanır. */
export const formatBookedDatePlusMinutesInTimezone = (
    backendDate,
    minutes,
    format,
    userTimezone = 'UTC',
    locale
) => {
    if (!backendDate) return '';
    try {
        const d = dayjs.utc(backendDate).add(minutes, 'minute').tz(userTimezone);
        if (!d.isValid()) return '';
        return locale ? d.locale(locale).format(format) : d.format(format);
    } catch (error) {
        console.warn('Tarih dönüştürme hatası:', error);
        return '';
    }
};

/** Kullanıcı timezone'unda saat (0–23); sabah/öğle/akşam gruplaması için. */
export const getBookedDateHourInTimezone = (backendDate, userTimezone = 'UTC') => {
    const d = toUserTimezoneDayjs(backendDate, userTimezone);
    return d ? d.hour() : 0;
};

/**
 * Doktor takvim API: seçili gün (yyyy-MM-dd) + doktor TZ startTime (HH:mm).
 * TZ verilirse wall-clock doktor diliminde çözülür.
 */
export const buildDoctorLocalSlotDateTime = (calendarDate, startTime, timeZone) => {
    if (!calendarDate || !startTime) return null;
    const dateKey =
        typeof calendarDate === 'string'
            ? calendarDate.trim().slice(0, 10)
            : dayjs(calendarDate).format('YYYY-MM-DD');
    try {
        const d = timeZone
            ? dayjs.tz(`${dateKey} ${startTime}`, 'YYYY-MM-DD HH:mm', timeZone)
            : dayjs(`${dateKey} ${startTime}`, 'YYYY-MM-DD HH:mm');
        return d.isValid() ? d : null;
    } catch (error) {
        console.warn('Doktor yerel slot tarihi hatası:', error);
        return null;
    }
};

/**
 * Slot geçmiş mi.
 * Tercihen bookedDate (UTC absolute); yoksa calendarDate+startTime doktor TZ.
 */
export const isDoctorLocalSlotExpired = (calendarDate, startTime, timeZone, bookedDate) => {
    if (bookedDate) {
        const d = dayjs(bookedDate);
        return d.isValid() && d.isBefore(dayjs(), 'minute');
    }
    const slotTime = buildDoctorLocalSlotDateTime(calendarDate, startTime, timeZone);
    if (!slotTime) return false;
    return slotTime.isBefore(dayjs(), 'minute');
};

/**
 * Slot saat etiketi: bookedDate her zaman doktor timezone'una çevrilir
 * (timezone değişince slot UTC'si aynı kalır, gösterim güncellenir).
 */
export const getDoctorCalendarSlotTimeLabel = (slot, userTimezone = 'UTC') => {
    if (slot?.bookedDate) {
        return formatBookedDateInTimezone(slot.bookedDate, 'HH:mm', userTimezone);
    }
    return slot?.startTime || '';
};

/**
 * Slot saat aralığı: bookedDate (+ süre) → doktor timezone.
 * startTime/endTime yalnızca bookedDate yoksa kullanılır.
 */
export const getDoctorCalendarSlotTimeRange = (slot, userTimezone = 'UTC', sessionDuration) => {
    if (slot?.bookedDate) {
        const start = formatBookedDateInTimezone(slot.bookedDate, 'HH:mm', userTimezone);
        let duration = sessionDuration ?? slot?.duration ?? 0;
        if (!duration && slot?.startTime && slot?.endTime) {
            const [sh, sm] = String(slot.startTime).split(':').map(Number);
            const [eh, em] = String(slot.endTime).split(':').map(Number);
            if ([sh, sm, eh, em].every((n) => !Number.isNaN(n))) {
                duration = eh * 60 + em - (sh * 60 + sm);
            }
        }
        if (!duration || duration <= 0) return start;
        const end = formatBookedDatePlusMinutesInTimezone(
            slot.bookedDate,
            duration,
            'HH:mm',
            userTimezone
        );
        return `${start} - ${end}`;
    }
    if (slot?.startTime && slot?.endTime) {
        return `${slot.startTime} - ${slot.endTime}`;
    }
    return slot?.startTime || '';
};

export const compareDoctorCalendarSlots = (a, b) => {
    if (a?.bookedDate && b?.bookedDate) {
        return dayjs(a.bookedDate).valueOf() - dayjs(b.bookedDate).valueOf();
    }
    if (a?.startTime && b?.startTime) {
        return a.startTime.localeCompare(b.startTime);
    }
    return dayjs(a?.bookedDate).valueOf() - dayjs(b?.bookedDate).valueOf();
};

/** auth.user.timeZone ile bookedDate formatlama. */
export const useFormatBookedDate = () => {
    const userTimezone = useUserTimezone();
    return useCallback(
        (backendDate, format, locale) =>
            formatBookedDateInTimezone(backendDate, format, userTimezone, locale),
        [userTimezone]
    );
};

/**
 * Redux store için date objelerini sanitize eder
 * dayjs objelerini ISO string'e çevirir
 * @param {Object} data - Sanitize edilecek data
 * @returns {Object} Sanitize edilmiş data
 */
export const sanitizeForRedux = (data) => {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const sanitized = { ...data };

    // birthDate: yalnızca takvim günü (YYYY-MM-DD), UTC/ISO kullanma
    if (sanitized.birthDate) {
        const asDay = formatBirthDateOnly(sanitized.birthDate);
        if (asDay) sanitized.birthDate = asDay;
    }

    // Diğer date field'larını da kontrol et
    const dateFields = ['createdAt', 'updatedAt', 'startDate', 'endDate', 'date'];
    dateFields.forEach(field => {
        if (sanitized[field]) {
            if (typeof sanitized[field].toISOString === 'function') {
                sanitized[field] = sanitized[field].toISOString();
            } else if (sanitized[field] instanceof Date) {
                sanitized[field] = sanitized[field].toISOString();
            }
        }
    });

    return sanitized;
};
